import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { MessageCircle, Send, ArrowLeft, User, UserCheck } from 'lucide-react';

export default function Questions({ user }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [content, setContent] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadQuestions();
  }, []);

  useEffect(() => {
    if (selectedQuestion) {
      loadMessages(selectedQuestion.question_id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        loadMessages(selectedQuestion.question_id);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedQuestion]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions`);
      setQuestions(res.data);
    } catch (error) {
      toast.error('載入提問失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (questionId) => {
    try {
      setLoadingMessages(true);
      const res = await axios.get(`${API}/questions/${questionId}/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('載入對話失敗:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error('請輸入提問內容');
      return;
    }

    try {
      await axios.post(`${API}/questions`, { content, media_urls: [] });
      toast.success('✅ 提問已送出，請等待回覆');
      setDialogOpen(false);
      setContent('');
      loadQuestions();
    } catch (error) {
      toast.error('送出失敗');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', newMessage);
      formData.append('media_urls', JSON.stringify([]));
      
      await axios.post(`${API}/questions/${selectedQuestion.question_id}/messages`, formData);
      setNewMessage('');
      loadMessages(selectedQuestion.question_id);
      loadQuestions();  // Refresh to update status
    } catch (error) {
      toast.error('發送失敗');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-TW', { 
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Conversation View
  if (selectedQuestion) {
    return (
      <div className="h-screen flex flex-col pt-16 md:pt-0" data-testid="question-conversation">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setSelectedQuestion(null)}
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h2 className="font-semibold text-gray-800">技術提問對話</h2>
            <p className="text-sm text-gray-500 line-clamp-1">{selectedQuestion.content}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            selectedQuestion.status === 'replied' 
              ? 'bg-green-100 text-green-700' 
              : 'bg-orange-100 text-orange-700'
          }`}>
            {selectedQuestion.status === 'replied' ? '已回覆' : '等待回覆'}
          </span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <div className="max-w-4xl mx-auto space-y-4">
            {loadingMessages && messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-500"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="mx-auto mb-2 text-gray-300" size={48} />
                <p>暫無對話，開始提問吧！</p>
              </div>
            ) : (
              messages.map((message) => {
                const isMe = message.sender_id === user.user_id;
                const isAdmin = ['super_admin', 'org_admin', 'coach'].includes(message.sender_role);
                
                return (
                  <div 
                    key={message.message_id} 
                    className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isAdmin ? 'bg-blue-500' : 'bg-orange-500'
                    } text-white flex-shrink-0`}>
                      {isAdmin ? <UserCheck size={20} /> : <User size={20} />}
                    </div>
                    
                    <div className={`flex-1 max-w-[70%] ${isMe ? 'items-end' : ''}`}>
                      <div className={`rounded-2xl px-4 py-3 ${
                        isMe 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-white text-gray-800 border border-gray-200'
                      }`}>
                        {!isMe && (
                          <p className="text-xs font-medium mb-1 opacity-70">
                            {isAdmin ? '管理員/教練' : '學生'}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                      <p className={`text-xs text-gray-500 mt-1 px-2 ${isMe ? 'text-right' : ''}`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="bg-white border-t border-gray-200 p-4">
          <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-2">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="輸入訊息..."
              className="flex-1 min-h-[60px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
            />
            <Button type="submit" disabled={!newMessage.trim()} className="h-[60px]">
              <Send size={20} />
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-2 text-center">
            按 Enter 發送，Shift + Enter 換行
          </p>
        </div>
      </div>
    );
  }

  // Questions List View
  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="questions-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-4xl font-bold text-gray-800">技術提問</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-500 hover:bg-blue-600">
                <MessageCircle className="mr-2" size={16} />
                新增提問
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>新增技術提問</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitQuestion} className="space-y-4">
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="請詳細描述您的問題..."
                  rows={6}
                  required
                />
                <Button type="submit" className="w-full">送出提問</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-500"></div>
          </div>
        ) : questions.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <MessageCircle className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">暫無提問</p>
            <p className="text-gray-400 text-sm mt-2">點擊右上角「新增提問」按鈕開始提問</p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((question) => (
              <div 
                key={question.question_id}
                onClick={() => setSelectedQuestion(question)}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-800 flex-1">
                    {question.content.substring(0, 100)}
                    {question.content.length > 100 && '...'}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ml-4 ${
                    question.status === 'replied' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {question.status === 'replied' ? '已回覆' : '等待回覆'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{formatTime(question.created_at)}</span>
                  <Button variant="ghost" size="sm" className="text-blue-600">
                    查看對話 →
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
